import React, { useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";

const Mixer = ({
  deckStates,
  crossfader,
  masterVolume,
  setCrossfader,
  setMasterVolume,
  setDeckVolume,
  setDeckEqLow,
  setDeckEqMid,
  setDeckEqHigh,
  setDeckFilter,
  setDeckStemVocal,
  setDeckStemInstrumental,
  setDeckStemBeats,
  setDeckStemMode,
  setDeckPan
}) => {
  const vuCanvasRef = useRef(null);

  // Local high-performance requestAnimationFrame loop to draw VU meters (0ms React re-render lag)
  useEffect(() => {
    let animId;
    const peakLeft = { val: 0 };
    const peakRight = { val: 0 };

    const drawVU = () => {
      const canvas = vuCanvasRef.current;
      if (!canvas) {
        animId = requestAnimationFrame(drawVU);
        return;
      }

      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.resetTransform();
      ctx.scale(dpr, dpr);

      ctx.fillStyle = "#0c0d12";
      ctx.fillRect(0, 0, w, h);

      let lvlLeft = 0;
      let lvlRight = 0;

      if (window.djEngine) {
        const engine = window.djEngine;
        engine.decks.forEach((deck, id) => {
          if (deck.isPlaying && deck.nodes.analyser) {
            const fftSize = deck.nodes.analyser.fftSize;
            const dataArray = new Uint8Array(fftSize);
            deck.nodes.analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < fftSize; i++) {
              const val = (dataArray[i] - 128) / 128;
              sum += val * val;
            }
            const rms = Math.sqrt(sum / fftSize);
            const valPercent = Math.min(100, Math.round(rms * 450 * deck.volume));

            if (id === "A" || id === "C") {
              lvlLeft = Math.max(lvlLeft, valPercent);
            } else if (id === "B" || id === "D") {
              lvlRight = Math.max(lvlRight, valPercent);
            }
          }
        });
      }

      // Smooth peak decay
      peakLeft.val = Math.max(lvlLeft, peakLeft.val - 1.5);
      peakRight.val = Math.max(lvlRight, peakRight.val - 1.5);

      const numSegments = 16;
      const padding = 1;
      const segH = (h - (numSegments + 1) * padding) / numSegments;
      const colW = (w - 3 * padding) / 2;

      const drawChannel = (level, peak, xOffset) => {
        const activeSegs = Math.round((level / 100) * numSegments);
        const peakSeg = Math.round((peak / 100) * numSegments);

        for (let i = 0; i < numSegments; i++) {
          const segIndex = numSegments - 1 - i; // Draw bottom to top
          const y = padding + i * (segH + padding);
          
          let color = "#1b4322"; // Off green
          if (segIndex < activeSegs) {
            if (segIndex < 3) color = "#ff2e63"; // Top red (clipping warnings)
            else if (segIndex < 7) color = "#ffeb3b"; // Mid yellow
            else color = "#00ff41"; // Safe green
          } else if (segIndex === peakSeg - 1 && peak > 0) {
            if (segIndex < 3) color = "#ff8a80"; // Peak indicator top
            else if (segIndex < 7) color = "#fff59d"; // Peak indicator mid
            else color = "#a5d6a7"; // Peak indicator bottom
          }

          ctx.fillStyle = color;
          ctx.fillRect(xOffset, y, colW, segH);
        }
      };

      drawChannel(lvlLeft, peakLeft.val, padding);
      drawChannel(lvlRight, peakRight.val, colW + 2 * padding);

      animId = requestAnimationFrame(drawVU);
    };

    drawVU();
    return () => cancelAnimationFrame(animId);
  }, []);

  // Helper render method for Standard Rotary Knobs (styled inputs)
  const renderKnob = (label, value, min, max, onChange, colorClass = "text-primary") => {
    const percent = (value - min) / (max - min);
    const rotationAngle = -135 + percent * 270;

    return (
      <div className="flex flex-col items-center select-none font-source">
        <span className="text-[8px] text-muted-foreground uppercase leading-none mb-1">{label}</span>
        <div 
          className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center relative cursor-ns-resize shadow-inner active:scale-95 transition"
          style={{ touchAction: "none" }}
          onDoubleClick={() => onChange(0)}
          onMouseDown={(e) => {
            const startY = e.clientY;
            const startVal = value;
            const handleMouseMove = (moveEvent) => {
              const deltaY = startY - moveEvent.clientY;
              const range = max - min;
              const step = range / 120; // vertical drag sensitivity
              const nextVal = Math.max(min, Math.min(max, startVal + deltaY * step));
              onChange(nextVal);
            };
            const handleMouseUp = () => {
              window.removeEventListener("mousemove", handleMouseMove);
              window.removeEventListener("mouseup", handleMouseUp);
            };
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
          }}
        >
          {/* Knob pointer line */}
          <div 
            className={`w-0.5 h-4 bg-current rounded-full absolute top-1 origin-bottom ${colorClass}`}
            style={{ transform: `rotate(${rotationAngle}deg)`, transformOrigin: "50% 100%" }}
          />
          {/* Inner ring */}
          <div className="w-6 h-6 rounded-full bg-zinc-950 border border-zinc-800 shadow"></div>
        </div>
        <span className="text-[9px] text-foreground font-bold mt-1 leading-none">{value.toFixed(0)}</span>
      </div>
    );
  };

  // Helper render method for VirtualDJ 3-Stem Knobs (Vocal, Instrumental, Beats)
  const renderStemKnob = (label, value, onChange, colorClass = "text-cyan-400", activeBg = "bg-cyan-500/20") => {
    const min = -100;
    const max = 100;
    const percent = (value - min) / (max - min);
    const rotationAngle = -135 + percent * 270;

    let modeBadge = "100%";
    let badgeStyle = "bg-zinc-800/80 text-zinc-400 border-zinc-700/50";

    if (value < -2) {
      modeBadge = `${Math.round(100 + value)}%`;
      badgeStyle = "bg-red-950/80 text-red-400 border-red-800/50";
    } else if (value > 2) {
      modeBadge = "SOLO";
      badgeStyle = "bg-emerald-950/80 text-emerald-400 border-emerald-800/50 animate-pulse";
    }

    return (
      <div className="flex flex-col items-center select-none font-source w-full">
        {/* Label & Status Badge */}
        <div className="flex items-center justify-between w-full px-1.5 mb-1">
          <span className={`text-[9px] font-bold uppercase tracking-wider ${colorClass}`}>{label}</span>
          <span className={`text-[7px] font-mono font-bold px-1 py-0.2 rounded border ${badgeStyle}`}>
            {modeBadge}
          </span>
        </div>
        
        {/* Interactive Rotary Knob */}
        <div 
          className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center relative cursor-ns-resize shadow-inner active:scale-95 transition"
          style={{ touchAction: "none" }}
          title={`${label}: Left = Decrease | Center = 100% Original | Right = Solo ${label}`}
          onDoubleClick={() => onChange(0)}
          onMouseDown={(e) => {
            const startY = e.clientY;
            const startVal = value;
            const handleMouseMove = (moveEvent) => {
              const deltaY = startY - moveEvent.clientY;
              const range = max - min;
              const step = range / 120;
              const nextVal = Math.max(min, Math.min(max, startVal + deltaY * step));
              onChange(nextVal);
            };
            const handleMouseUp = () => {
              window.removeEventListener("mousemove", handleMouseMove);
              window.removeEventListener("mouseup", handleMouseUp);
            };
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
          }}
        >
          {/* Center 100% Notch Indicator */}
          <div className="w-0.5 h-1 bg-zinc-700 absolute top-0.5 rounded-full" />

          {/* Rotary Pointer Line */}
          <div 
            className={`w-0.5 h-4 bg-current rounded-full absolute top-1 origin-bottom ${colorClass}`}
            style={{ transform: `rotate(${rotationAngle}deg)`, transformOrigin: "50% 100%" }}
          />
          {/* Inner Core */}
          <div className="w-5 h-5 rounded-full bg-zinc-950 border border-zinc-800 shadow flex items-center justify-center">
            <div className={`w-1.5 h-1.5 rounded-full bg-current ${colorClass} opacity-80`} />
          </div>
        </div>

        {/* VirtualDJ Quick Action Buttons: CUT (-100), 100% (0), SOLO (+100) */}
        <div className="flex items-center gap-1 mt-1">
          <button
            type="button"
            onClick={() => onChange(-100)}
            className={`text-[7px] font-mono px-1 py-0.5 rounded border transition ${value <= -95 ? 'bg-red-600 text-white font-bold border-red-400 shadow' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200'}`}
            title={`Cut ${label} completely (0%)`}
          >
            CUT
          </button>
          <button
            type="button"
            onClick={() => onChange(0)}
            className={`text-[7px] font-mono px-1 py-0.5 rounded border transition ${Math.abs(value) <= 2 ? 'bg-zinc-800 text-zinc-200 font-bold border-zinc-600' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200'}`}
            title={`Reset ${label} to 100% Original`}
          >
            100%
          </button>
          <button
            type="button"
            onClick={() => onChange(100)}
            className={`text-[7px] font-mono px-1 py-0.5 rounded border transition ${value >= 95 ? 'bg-emerald-600 text-white font-bold border-emerald-400 shadow' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200'}`}
            title={`Solo ${label} only (Mute all other stems)`}
          >
            SOLO
          </button>
        </div>
      </div>
    );
  };

  // Channel Strip Renderer helper
  const renderChannelStrip = (deckId, title, borderSide = "right") => {
    const deckState = deckStates[deckId];
    const mode = deckState?.stems?.mode || "stems";

    return (
      <div className={`col-span-4 ${borderSide === "right" ? "border-r pr-2" : "border-l pl-2"} border-border/40 flex flex-col justify-between items-center h-full space-y-2`}>
        <div className="flex items-center justify-between border-b border-zinc-800 pb-1 w-full px-1">
          <span className="text-[10px] font-fira text-primary font-bold uppercase tracking-widest">
            {title}
          </span>

          {/* EQ vs STEMS Mode Switcher (VirtualDJ Style) */}
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded p-0.5">
            <button
              type="button"
              onClick={() => setDeckStemMode && setDeckStemMode(deckId, "stems")}
              className={`text-[7px] font-bold px-1.5 py-0.5 rounded transition ${mode === "stems" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              title="VirtualDJ 3-Stem Mode (Vocal, Instrumental, Beats)"
            >
              STEMS
            </button>
            <button
              type="button"
              onClick={() => setDeckStemMode && setDeckStemMode(deckId, "eq")}
              className={`text-[7px] font-bold px-1.5 py-0.5 rounded transition ${mode === "eq" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              title="Classic 3-Band EQ Mode (High, Mid, Low)"
            >
              EQ
            </button>
          </div>
        </div>

        {/* Mixer Knobs: STEMS vs EQ */}
        <div className="flex flex-col space-y-2 items-center w-full flex-grow justify-start pt-1">
          {mode === "stems" ? (
            <>
              {/* Vocal Stem Control: Left = Decrease, Center = 100%, Right = Solo Vocal */}
              {renderStemKnob(
                "Vocal",
                deckState?.stems?.vocal ?? 0,
                (val) => setDeckStemVocal && setDeckStemVocal(deckId, val),
                "text-cyan-400"
              )}

              {/* Instrumental Stem Control: Left = Decrease, Center = 100%, Right = Solo Instrumental */}
              {renderStemKnob(
                "Instrumental",
                deckState?.stems?.instrumental ?? 0,
                (val) => setDeckStemInstrumental && setDeckStemInstrumental(deckId, val),
                "text-purple-400"
              )}

              {/* Beats Stem Control: Left = Decrease, Center = 100%, Right = Solo Beats */}
              {renderStemKnob(
                "Beats",
                deckState?.stems?.beats ?? 0,
                (val) => setDeckStemBeats && setDeckStemBeats(deckId, val),
                "text-amber-400"
              )}
            </>
          ) : (
            <>
              {renderKnob("High EQ", deckState?.fxConfig.eqHigh || 0, -12, 12, (val) => setDeckEqHigh(deckId, val))}
              {renderKnob("Mid EQ", deckState?.fxConfig.eqMid || 0, -12, 12, (val) => setDeckEqMid(deckId, val))}
              {renderKnob("Low EQ", deckState?.fxConfig.eqLow || 0, -12, 12, (val) => setDeckEqLow(deckId, val))}
            </>
          )}

          {/* Filter HP/LP (Always Available) */}
          {renderKnob(
            "Filter HP/LP",
            deckState?.fxConfig.filterCutoff ? (deckState.fxConfig.filterType === "highpass" ? (deckState.fxConfig.filterCutoff - 20) / 39.8 : -(20000 - deckState.fxConfig.filterCutoff) / 198) : 0,
            -100,
            100,
            (val) => setDeckFilter(deckId, val),
            "text-terminal-cyan"
          )}
        </div>

        {/* Volume Slider (vertical) */}
        <div className="flex flex-col items-center w-full pb-1 mt-2">
          <span className="text-[8px] text-muted-foreground uppercase mb-1 font-source">Fader</span>
          <div className="relative h-24 w-6 bg-zinc-950 border border-zinc-800 rounded flex justify-center py-2 shadow-inner">
            <input
              type="range"
              orient="vertical"
              min="0"
              max="1.0"
              step="0.01"
              value={deckState?.volume ?? 0.8}
              onChange={(e) => setDeckVolume(deckId, parseFloat(e.target.value))}
              className="w-1.5 h-full cursor-pointer accent-primary"
              style={{ WebkitAppearance: "slider-vertical" }}
            />
          </div>
          <span className="text-[10px] font-fira font-bold text-foreground mt-1">
            {Math.round((deckState?.volume ?? 0.8) * 100)}%
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 flex flex-col justify-between h-full relative overflow-hidden terminal-glow">
      {/* Neon glowing elements */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/10 via-primary/50 to-primary/10"></div>

      {/* Grid: Mix Strips A & B + Master Section */}
      <div className="grid grid-cols-12 gap-2 flex-grow h-[calc(100%-80px)]">
        
        {/* Channel Strip A */}
        {renderChannelStrip("A", "CH A", "right")}

        {/* Master Output & VU meters (Center Channel) */}
        <div className="col-span-4 flex flex-col justify-between items-center h-full px-1">
          <div className="text-[10px] font-fira text-terminal-cyan font-bold uppercase tracking-widest border-b border-zinc-800 pb-1 w-full text-center">
            MASTER
          </div>

          {/* Dynamic LED VU Column */}
          <div className="flex-grow flex flex-col justify-center items-center py-2 h-0 w-full relative">
            <div className="absolute left-0 text-[7px] font-fira text-muted-foreground/50 h-full flex flex-col justify-between pr-1 select-none pointer-events-none py-2 text-right">
              <span>CLIP</span>
              <span>+6</span>
              <span>0dB</span>
              <span>-3</span>
              <span>-12</span>
              <span>-30</span>
            </div>
            <div className="w-6 h-[85%] border border-zinc-800 rounded bg-zinc-950 p-0.5 overflow-hidden shadow-inner ml-4">
              <canvas ref={vuCanvasRef} className="w-full h-full" />
            </div>
          </div>

          {/* Master Fader */}
          <div className="flex flex-col items-center w-full mt-2">
            <span className="text-[8px] text-muted-foreground uppercase mb-1 font-source">Master Volume</span>
            <div className="relative h-24 w-6 bg-zinc-950 border border-zinc-800 rounded flex justify-center py-2 shadow-inner">
              <input
                type="range"
                orient="vertical"
                min="0"
                max="1.0"
                step="0.01"
                value={masterVolume}
                onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
                className="w-1.5 h-full cursor-pointer accent-terminal-cyan"
                style={{ WebkitAppearance: "slider-vertical" }}
              />
            </div>
            <span className="text-[10px] font-fira font-bold text-terminal-cyan mt-1">
              {Math.round(masterVolume * 100)}%
            </span>
          </div>
        </div>

        {/* Channel Strip B */}
        {renderChannelStrip("B", "CH B", "left")}
      </div>

      {/* Crossfader Section (Horizontal) */}
      <div className="flex flex-col items-center w-full mt-4 border-t border-border/40 pt-3">
        <div className="flex justify-between w-full text-[8px] font-source text-muted-foreground px-4 mb-1">
          <span>DECK A (LEFT)</span>
          <span className="text-center font-bold">CROSSFADER</span>
          <span>DECK B (RIGHT)</span>
        </div>
        <div className="w-[85%] relative flex items-center justify-center bg-zinc-950 border border-zinc-800 rounded-full h-5 px-3 py-1 shadow-inner">
          <input
            type="range"
            min="-1.0"
            max="1.0"
            step="0.01"
            value={crossfader}
            onChange={(e) => setCrossfader(parseFloat(e.target.value))}
            className="w-full h-1 cursor-pointer accent-primary bg-zinc-800 rounded-lg outline-none"
          />
        </div>
      </div>
    </div>
  );
};

export default Mixer;
